import React, { useState, useEffect, useRef } from 'react';

/**
 * AddressAutocomplete
 * 
 * Search-as-you-type address input backed by the self-hosted
 * addresstify service (G-NAF data). Returns validated addresses with
 * coordinates and structured components.
 * 
 * Props:
 *   value        — current address text (controlled)
 *   onChange     — (text) => void, called as the user types
 *   onSelect     — (result) => void, called when a suggestion is picked
 *   placeholder  — input placeholder
 *   required     — HTML required flag
 *   className    — extra classes for the outer wrapper
 *   minChars     — minimum characters before searching (default 3)
 *   limit        — max results to show (default 5)
 */
export default function AddressAutocomplete({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Start typing your address...',
  required = false,
  className = '',
  minChars = 3,
  limit = 5,
}) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Keep internal query in sync with the controlled value
  useEffect(() => {
    if (value !== query) {
      setQuery(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced search
  useEffect(() => {
    if (query.length < minChars) {
      setResults([]);
      setOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/address/search?q=${encodeURIComponent(query)}&limit=${limit}`
        );
        if (!res.ok) throw new Error('Search failed');
        const data = await res.json();
        setResults(data.results || []);
        setOpen((data.results || []).length > 0);
        setHighlighted(0);
      } catch (err) {
        console.error('Address search error:', err);
        setError('Could not search addresses. Please try again.');
        setResults([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, minChars, limit]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleInputChange(e) {
    const val = e.target.value;
    setQuery(val);
    if (onChange) onChange(val);
  }

  function selectResult(result) {
    setQuery(result.address);
    setOpen(false);
    // IMPORTANT: onChange must run before onSelect.
    // Both call setFormData in the parent; React batches them
    // and the second call's `prev` would not see the first
    // call's changes if the order were reversed, wiping pid.
    if (onChange) onChange(result.address);
    if (onSelect) onSelect(result);
    if (inputRef.current) inputRef.current.blur();
  }

  function handleKeyDown(e) {
    if (!open) {
      // Allow opening with down arrow if we have results
      if (e.key === 'ArrowDown' && results.length > 0) {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[highlighted]) selectResult(results[highlighted]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  function handleFocus() {
    if (results.length > 0) setOpen(true);
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          className="w-full border rounded-md px-3 py-2 pr-9"
        />
        {loading && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
            ⏳
          </span>
        )}
      </div>

      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}

      {open && results.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
          {results.map((r, i) => (
            <li
              key={r.pid || i}
              onClick={() => selectResult(r)}
              onMouseEnter={() => setHighlighted(i)}
              className={`px-3 py-2 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 ${
                i === highlighted ? 'bg-green-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="font-medium text-gray-800">{r.address}</div>
              {r.components?.suburb && (
                <div className="text-xs text-gray-500 mt-0.5">
                  {r.components.suburb} {r.components.state} {r.components.postcode}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {open && !loading && query.length >= minChars && results.length === 0 && !error && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2 text-sm text-gray-500">
          No matching addresses found
        </div>
      )}
    </div>
  );
}
